import { type ProjectIdentity, readProjectIdentity } from "../bridge/projectIdentity";
import type { HostAdapter } from "../host/adapter";

/**
 * Follow the host's project, and report when it moves (#67).
 *
 * The control app answers `giraffe_sdk_*` for the active project, and the relay
 * keeps it eligible for the project its token names. If the panel survives an
 * in-tab project switch and keeps reporting the old project, the relay keeps
 * that project active while the host underneath shows another — so a write
 * meant for one lands in the other. Reporting the move lets the bridge re-dial
 * with a token for the new project, as a carrier's `projectChanged` does.
 *
 * Reads are serialised and the latest one wins: a push that lands mid-read is
 * read again rather than dropped. Returns the unsubscribe.
 */
export function followProject(
  host: HostAdapter,
  initial: ProjectIdentity,
  onMove: (next: ProjectIdentity) => void,
): () => void {
  let current = initial;
  let reading = false;
  let readAgain = false;
  let stopped = false;

  const read = async (): Promise<void> => {
    if (reading) {
      readAgain = true;
      return;
    }
    reading = true;
    try {
      do {
        readAgain = false;
        const located = await readProjectIdentity(host);
        if (stopped || !located.ok) continue;
        const next = located.identity;
        if (
          next.projectId === current.projectId &&
          next.workspaceId === current.workspaceId &&
          next.projectUrl === current.projectUrl
        ) {
          continue;
        }
        current = next;
        onMove(next);
      } while (readAgain && !stopped);
    } finally {
      reading = false;
    }
  };

  const unsubscribe = host.onStateChange(["project"], () => {
    read().catch((cause: unknown) => {
      console.log({
        event: "control.project_read_failed",
        message: cause instanceof Error ? cause.message : String(cause),
      });
    });
  });
  return () => {
    stopped = true;
    unsubscribe();
  };
}
